<script setup>
import { ref, inject, onMounted } from 'vue';
import { useProgramStore } from '@/stores/program';
import { storeToRefs } from 'pinia';
import { useDateFormatter } from '@/composables/dateFormatter';
import { router } from '@inertiajs/vue3';

const { search, destroy, createBlankProgram } = useProgramStore();
const { all } = storeToRefs(useProgramStore());
const { formatDate } = useDateFormatter();

const params = ref({
    term: '',
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchProgram = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchProgram();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchProgram();
};

const openModal = (modal, mode = 'Create', program = null) => {
    modals.show(modal, {
        props: program ? {
            program: program,
            mode: mode,
        } : {
            mode: mode,
        }
    }).then(() => {
        searchProgram();
    });
};

const copyProgram = async (program) => {
    openModal('ProgramsModal', 'Copy', {
        ...program,
        program_name: `${program.program_name} (Copy)`,
    });
};

const remove = async (program) => {
    const confirm = await modals.show('ConfirmationModal', {
        props: {
            title: 'Confirm Deletion',
            prompt: 'Are you sure you want to delete this program?',
        },
    });

    if (confirm) {
        await destroy({ hash: program.hash });
        searchProgram();
    }
};

const createProgram = async () => {
    const response = await createBlankProgram();
    const program = response.data.program;
    router.visit(`/programs/${program.hash}/edit`);
};

const visitProgramEdit = (program) => {
    router.visit(`/programs/${program.hash}/edit`);
};

onMounted(() => {
    searchProgram();
});
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.program_management') }}</template>
    <template #actions>
        <StyledButton @click="createProgram"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{  $t('label.new_program') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchProgram"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.status"
            clearable
            :placeholder="$t('label.status')"
            :options="[
                { label: $t('label.active'), value: true },
                { label: $t('label.inactive'), value: false },
            ]"
            name="status"
            @input="searchProgram"
            @click:clear="searchProgram" />
    </div>
    <template v-if="all && all.data.length">
        <DataTable
            :data="all"
            :headers="[
                { title: $t('label.lender_name'), key: 'lender_name', value: item => item.lender.code, sortable: false },
                { title: $t('label.program_name'), key: 'program_name', value: item => item.program_name, sortable: false },
                { title: $t('label.age_of_vehicle'), key: 'vehicle_years', value: item => item.vehicle_min_year + ' - ' + item.vehicle_max_year, sortable: false },
                { title: $t('label.effective_date'), key: 'effective_date', value: item => item.effective_date ? formatDate(item.effective_date) : '', sortable: false },
                { title: $t('label.end_date'), key: 'end_date', value: item => item.end_date ? formatDate(item.end_date) : '', sortable: false },
                { title: $t('label.status'), key: 'status', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
            ]"
            :actions="[
                { title: $t('label.duplicate'), icon: 'mdi-content-copy', action: (item) => copyProgram(item) },
                { title: $t('label.edit'), icon: 'mdi-pencil', action: (item) => visitProgramEdit(item) },
                { title: $t('label.delete'), icon: 'mdi-delete', action: (item) => remove(item) }
            ]"  
            @table-update="tableUpdate" />
    </template>
    <template v-else>
        <div class="text-center text-gray-500 py-8">{{ $t('label.no_programs_found') }}</div>
    </template>
</MainContentLayout>
</template>
